import React, { useEffect, useMemo, useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { AnimatePresence, motion, type Variants } from 'framer-motion';
import { Link } from 'react-router-dom';
import { fetchProductsRequest } from '../../integrations/woocommerce/productSlice';
import type { Product as WooProduct } from '../../integrations/woocommerce/types';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import type { RootState } from '../../store';

const LIVE_SEARCH_MIN_CHARS = 2;
const LIVE_SEARCH_DEBOUNCE_MS = 500;

interface SearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

const containerVariants: Variants = {
  hidden: { opacity: 0, scale: 0.98 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.25, ease: [0.16, 1, 0.3, 1] },
  },
  exit: {
    opacity: 0,
    scale: 0.98,
    transition: { duration: 0.2, ease: [0.4, 0, 1, 1] },
  },
};

const resultsVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.08,
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.2, ease: [0.16, 1, 0.3, 1] } },
};

const formatPrice = (product: WooProduct) => {
  const numericPrice = Number(product.price || product.regular_price || 0);

  if (!Number.isFinite(numericPrice) || numericPrice <= 0) {
    return 'Price on request';
  }

  return `LKR ${numericPrice.toLocaleString()}`;
};

const normalizeSearchValue = (value?: string | number): string => String(value ?? '').toLowerCase();

const productMatchesQuery = (product: WooProduct, query: string): boolean => {
  const normalizedQuery = normalizeSearchValue(query).trim();
  if (!normalizedQuery) return true;

  const searchableValues = [
    product.name,
    product.slug,
    ...(product.categories || []).flatMap((category) => [category.name, category.slug]),
    ...(product.tags || []).flatMap((tag) => [tag.name, tag.slug]),
  ];

  return searchableValues.some((value) => normalizeSearchValue(value).includes(normalizedQuery));
};

const SearchOverlay: React.FC<SearchOverlayProps> = ({ isOpen, onClose }) => {
  const dispatch = useAppDispatch();
  const { items, loading } = useAppSelector((state: RootState) => state.products);

  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const body = document.body;
    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const normalized = query.trim();

    if (normalized.length < LIVE_SEARCH_MIN_CHARS) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      dispatch(fetchProductsRequest({ searchQuery: normalized, perPage: 12 }));
    }, LIVE_SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [dispatch, isOpen, query]);

  const products = useMemo(() => {
    const normalized = query.trim().toLowerCase();

    if (!normalized) {
      return [];
    }

    return items.filter((product: WooProduct) => productMatchesQuery(product, normalized)).slice(0, 24);
  }, [items, query]);

  return (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          key="search-overlay"
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={containerVariants}
          className="fixed inset-0 z-[1400] bg-slate-950/75 backdrop-blur-md"
          onClick={onClose}
          aria-modal="true"
          role="dialog"
        >
          <div className="flex h-full w-full items-start justify-center p-3 pt-10 sm:p-5 md:p-10">
            <motion.div
              className="w-full max-w-4xl"
              onClick={(event) => event.stopPropagation()}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0, transition: { delay: 0.05, duration: 0.25 } }}
              exit={{ opacity: 0, y: 10 }}
            >
              <div className="rounded-3xl border border-white/20 bg-white/10 p-4 shadow-[0_30px_80px_rgba(0,0,0,0.45)] backdrop-blur-xl md:p-6">
                <div className="flex items-center gap-3 rounded-2xl border border-white/15 bg-black/25 px-4 py-3 md:px-5 md:py-4">
                  <span className="shrink-0 text-slate-300" aria-hidden="true">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M11 4a7 7 0 1 0 4.39 12.45l3.58 3.58a1 1 0 0 0 1.41-1.41l-3.58-3.58A7 7 0 0 0 11 4Zm0 2a5 5 0 1 1 0 10 5 5 0 0 1 0-10Z"
                        fill="currentColor"
                      />
                    </svg>
                  </span>
                  <input
                    autoFocus
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search products, brands, or specs..."
                    className="w-full bg-transparent text-lg font-medium text-white placeholder:text-slate-300/85 focus:outline-none md:text-2xl"
                    aria-label="Search products"
                  />
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-full border border-white/20 bg-white/10 p-2 text-white transition hover:bg-white/20"
                    aria-label="Close search"
                  >
                    <span aria-hidden="true">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M6.7 5.3a1 1 0 0 0-1.4 1.4L10.6 12l-5.3 5.3a1 1 0 1 0 1.4 1.4l5.3-5.3 5.3 5.3a1 1 0 0 0 1.4-1.4L13.4 12l5.3-5.3a1 1 0 1 0-1.4-1.4L12 10.6 6.7 5.3Z"
                          fill="currentColor"
                        />
                      </svg>
                    </span>
                  </button>
                </div>

                <div className="mt-4 max-h-[68vh] overflow-y-auto pr-1 md:mt-6">
                  {query.trim().length < LIVE_SEARCH_MIN_CHARS ? (
                    <p className="px-2 py-8 text-center text-sm text-slate-300 md:text-base">
                      Type at least 2 characters to start searching.
                    </p>
                  ) : loading ? (
                    <p className="animate-pulse px-2 py-8 text-center text-sm text-slate-200 md:text-base">Searching products...</p>
                  ) : products.length === 0 ? (
                    <p className="px-2 py-8 text-center text-sm text-slate-300 md:text-base">No products found for "{query.trim()}".</p>
                  ) : (
                    <motion.ul
                      variants={resultsVariants}
                      initial="hidden"
                      animate="visible"
                      className="grid grid-cols-1 gap-3 md:grid-cols-2"
                    >
                      {products.map((product: WooProduct) => (
                        <motion.li key={product.id} variants={itemVariants}>
                          <Link
                            to={product.slug ? `/product/${product.slug}` : '#'}
                            onClick={onClose}
                            className="flex items-center gap-3 rounded-2xl border border-white/15 bg-white/5 p-3 transition hover:bg-white/10"
                          >
                            <LazyLoadImage
                              src={product.images?.[0]?.src || '/assets/images/placeholders/product-placeholder.jpg'}
                              alt={product.images?.[0]?.alt || product.name}
                              effect="blur"
                              className="h-14 w-14 rounded-xl object-cover"
                              wrapperClassName="h-14 w-14 shrink-0"
                            />
                            <div className="min-w-0">
                              <p className="line-clamp-2 text-sm font-semibold text-white md:text-base">{product.name}</p>
                              <p className="mt-1 text-xs font-medium text-cyan-200 md:text-sm">{formatPrice(product)}</p>
                            </div>
                          </Link>
                        </motion.li>
                      ))}
                    </motion.ul>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

export default SearchOverlay;
